/**
 * Taking a published layout into this workspace — the copy that "applying" is.
 *
 * A club publishes an arrangement; a member applies it and gets a layout of
 * their own that they can then change. The whole of that is here, and it is one
 * job: **every id in the tree is minted fresh, and every reference between them
 * is repointed at the new one.**
 *
 * ## Why it cannot just be the definition with a new `Layout.id`
 *
 * The workspace is stored relationally and every node is addressed by its ULID
 * (`external_id`). Two layouts in one workspace carrying the same `Pane.id` are
 * two rows claiming one identity, and the upsert that keeps existing rows would
 * map them onto each other — so a member who applied the same preset twice, or
 * two presets a club built from one screen, would find one of them editing the
 * other. The failure is silent and it is in the store, not on screen.
 *
 * ## Why the references have to move with them
 *
 * Four fields point at another node *by id*, and a re-mint that ignored them
 * would leave each one pointing into the publisher's tree:
 *
 * | field | points at |
 * |---|---|
 * | `Plot.axisRef` | an `AxisInfo.id` in the same pane |
 * | `Plot.signal.anchorRef` | a `Plot.id` in the same pane |
 * | `ScreenerConfig.activeFilterRef` | a `SavedScreenerFilter.id` |
 * | `ScreenerConfig.activeFilterRefs` | the same, several |
 *
 * `Plot.indicatorRef` is deliberately **not** in that list: it names an
 * indicator spec by shortname, not a node in this tree, so re-minting it would
 * break the plot rather than fix it.
 *
 * ## A reference that resolves to nothing is dropped, not kept
 *
 * A published definition can carry a ref whose target was already gone. Keeping
 * the old id would point at a node that exists in no workspace at all; dropping
 * it lets each field degrade the way the contract says it should — a plot falls
 * back to its pane's own axis, a signal to the pane's primary plot
 * (`resolveAnchorRef` already does exactly this), a filter to an unnamed ad-hoc
 * one.
 */

import { mintUlid, type Layout, type Pane } from "@qufolio/model";
import { isMacroKind } from "./macro-sections";

/** Old id to new. Built as the tree is walked, applied to the refs after. */
type IdMap = Map<string, string>;

function remint(map: IdMap, id: unknown): string {
  const fresh = mintUlid();
  if (typeof id === "string" && id) map.set(id, fresh);
  return fresh;
}

/** The new id for a reference, or `undefined` when it pointed at nothing. */
function repoint(map: IdMap, ref: unknown): string | undefined {
  return typeof ref === "string" ? map.get(ref) : undefined;
}

function adoptPlot(map: IdMap, plot: Record<string, unknown>): Record<string, unknown> {
  const signal = plot.signal as Record<string, unknown> | undefined;
  return {
    ...plot,
    id: remint(map, plot.id),
    // **Copied, not shared.** The second pass repoints `anchorRef` in place, and
    // a spread leaves `signal` pointing at the caller's own object — so adopting
    // would edit the definition it was handed, and applying the same preset
    // twice would find its refs already rewritten to the first copy's ids. Every
    // other node here is replaced outright; this is the one that is nested.
    ...(signal ? { signal: { ...signal } } : {}),
  };
}

function adoptPane(map: IdMap, pane: unknown): Record<string, unknown> {
  const node = (pane ?? {}) as Record<string, unknown>;
  const axes = Array.isArray(node.axes) ? node.axes : [];
  const plots = Array.isArray(node.plots) ? node.plots : [];
  const annotations = Array.isArray(node.annotations) ? node.annotations : [];
  return {
    ...node,
    id: remint(map, node.id),
    // Axes before plots: a plot's `axisRef` names one of these, and the
    // second pass below can only repoint what the first pass has minted.
    axes: axes.map((axis) => {
      const each = (axis ?? {}) as Record<string, unknown>;
      return { ...each, id: remint(map, each.id) };
    }),
    plots: plots.map((plot) => adoptPlot(map, (plot ?? {}) as Record<string, unknown>)),
    annotations: annotations.map((annotation) => {
      const each = (annotation ?? {}) as Record<string, unknown>;
      return { ...each, id: remint(map, each.id) };
    }),
  };
}

function adoptChartGroup(map: IdMap, config: Record<string, unknown>): Record<string, unknown> {
  const panes = Array.isArray(config.panes) ? config.panes : [];
  return {
    ...config,
    id: remint(map, config.id),
    panes: panes.map((pane) => adoptPane(map, pane)),
  };
}

/** `axisRef` and `anchorRef`, the two refs that live inside a pane. */
function repointPanes(map: IdMap, panes: unknown): void {
  for (const pane of (Array.isArray(panes) ? panes : []) as Record<string, unknown>[]) {
    for (const plot of (Array.isArray(pane.plots) ? pane.plots : []) as Record<
      string,
      unknown
    >[]) {
      if ("axisRef" in plot) {
        const moved = repoint(map, plot.axisRef);
        if (moved) plot.axisRef = moved;
        else delete plot.axisRef;
      }
      const signal = plot.signal as Record<string, unknown> | undefined;
      if (signal && "anchorRef" in signal) {
        const moved = repoint(map, signal.anchorRef);
        if (moved) signal.anchorRef = moved;
        else delete signal.anchorRef;
      }
    }
  }
}

/**
 * A set of panes with every id minted fresh and their refs repointed — the
 * chart-sized half of `adoptLayout`, for a chart template applied onto a chart
 * that keeps its own `ChartGroup.id`.
 *
 * Minting the ids without the second pass is the failure this exists to
 * prevent: every plot keeps an `axisRef` naming an axis that is no longer
 * there, and each pane draws an empty scale.
 */
export function adoptPanes(panes: readonly Pane[]): Pane[] {
  const map: IdMap = new Map();
  const adopted = panes.map((pane) => adoptPane(map, pane));
  repointPanes(map, adopted);
  return adopted as unknown as Pane[];
}

function adoptScreener(map: IdMap, config: Record<string, unknown>): Record<string, unknown> {
  const saved = Array.isArray(config.savedFilters) ? config.savedFilters : [];
  return {
    ...config,
    id: remint(map, config.id),
    savedFilters: saved.map((filter) => {
      const each = (filter ?? {}) as Record<string, unknown>;
      // **A sealed condition keeps its id.** It is not this workspace's to mint:
      // it names a rule in its screen's published library, which the server
      // looks up by exactly that id, so a fresh one asks for a rule nobody has.
      // Mapped to itself, so a tick of it survives the repoint pass.
      if (each.sealed === true) {
        if (typeof each.id === "string" && each.id) map.set(each.id, each.id);
        return each;
      }
      return { ...each, id: remint(map, each.id) };
    }),
  };
}

/**
 * A Daily Brief as published: the preset only. `mine` is one member's board,
 * so it never travels — not from a member's layout saved as a template, nor
 * from an author's board through a publish (`docs/plans/daily-brief.md`).
 * The widget ids are not this tree's nodes and are kept: a republish pairs a
 * member's board with the fresh one by them (`group-layouts.withReplaced`).
 */
function adoptBriefBoard(config: Record<string, unknown>): Record<string, unknown> {
  const { mine: _mine, mineBasedOn: _basedOn, ...rest } = config;
  return rest;
}

/**
 * A macro section's settings, copied as they are (`lib/macro-sections.ts`).
 * They hold no node of this tree — no id to mint, nothing to repoint — and
 * going through `adoptChartGroup` would graft a chart's `id` and `panes` onto a
 * config whose schema refuses both.
 */
function adoptMacro(config: Record<string, unknown>): Record<string, unknown> {
  return structuredClone(config);
}

/** The second pass: every reference, now that every id it could name exists. */
function repointRefs(map: IdMap, section: Record<string, unknown>): void {
  const config = (section.config ?? {}) as Record<string, unknown>;

  repointPanes(map, config.panes);

  if ("activeFilterRef" in config) {
    const moved = repoint(map, config.activeFilterRef);
    if (moved) config.activeFilterRef = moved;
    else delete config.activeFilterRef;
  }
  if (Array.isArray(config.activeFilterRefs)) {
    const moved = config.activeFilterRefs
      .map((ref) => repoint(map, ref))
      .filter((ref): ref is string => Boolean(ref));
    if (moved.length > 0) config.activeFilterRefs = moved;
    else delete config.activeFilterRefs;
  }
}

/**
 * A published definition as a layout this workspace owns.
 *
 * `name` is the caller's, not the definition's: the preset carries the club's
 * name for it, and a member applying two of them wants to be able to tell the
 * tabs apart.
 */
export function adoptLayout(definition: Partial<Layout>, name: string): Layout {
  const map: IdMap = new Map();
  const raw = (definition ?? {}) as Record<string, unknown>;
  const sections = Array.isArray(raw.sections) ? raw.sections : [];

  const adopted = sections.map((section) => {
    const node = { ...((section ?? {}) as Record<string, unknown>) };
    node.id = remint(map, node.id);
    const config = (node.config ?? {}) as Record<string, unknown>;
    node.config =
      node.kind === "screener"
        ? adoptScreener(map, config)
        : node.kind === "briefBoard"
          ? adoptBriefBoard(config)
          : typeof node.kind === "string" && isMacroKind(node.kind)
            ? adoptMacro(config)
            : adoptChartGroup(map, config);
    return node;
  });

  for (const section of adopted) repointRefs(map, section);

  return {
    ...raw,
    id: mintUlid(),
    name,
    // Defaulted rather than trusted: a definition that lost its grid would
    // otherwise produce a layout the renderer divides by zero over. One cell is
    // the shape `addLayout` already starts from.
    rows: typeof raw.rows === "number" && raw.rows > 0 ? raw.rows : 1,
    columns: typeof raw.columns === "number" && raw.columns > 0 ? raw.columns : 1,
    sections: adopted,
  } as unknown as Layout;
}
